import {
    ResultObj,
    allFetch,
} from "../Domain";

/**
 * @description 零件標籤新建參數
 * @param {string} label_big 第一層流水號
 * @param {string} label_mid 第二層流水號
 * @param {string} content 標籤名稱
 * @param {string} type 零件類別
 * @param {string} is_front 是否前台用
 * @param {string} is_nav 是否放在nav
 * @param {string} seq 排序
 */
export interface CommodityLabelCreateParams {
    label_big: string;
    label_mid: string;
    content?: string;
    type?: string;
    is_front?: string;
    is_nav?: string;
    seq?: string;
}

/**
 * @description 零件標籤新建
 */
export const CommodityLabelCreate = async (
    obj: CommodityLabelCreateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityLabelCreate', obj);
};

/**
 * @description 零件標籤查詢參數
 * @param {string} label_big 第一層流水號
 * @param {string} label_mid 第二層流水號
 * @param {string} content 標籤名稱
 * @param {string} visible 是否可見
 * @param {string} type 零件類別
 * @param {string} is_front 是否前台用
 * @param {string} is_nav 是否放在nav
 */
export interface CommodityLabelSearchParams {
    label_big: string;
    label_mid: string;
    content: string;
    visible: "Y" | "N" | "";
    type: "配件" | "零件" | "設備" | '非原廠配件';
    is_front: "Y" | "N";
    is_nav: "Y" | "N";
}

export type CommodityLabelBigItem = {
    is_nav: "Y" | "N"
} & CommodityLabelMidItem

export type CommodityLabelMidItem = {

} & CommodityLabelSmallItem

export type CommodityLabelSmallItem = {
    cl_id: string;
    content: string;
    is_front: "Y" | "N";
    label_big: string;
    label_mid: string;
    seq: number;
    type: "配件" | "零件";
    visible: "Y" | "N";
}

/**
 * @description 零件標籤查詢結果
 */
export interface CommodityLabelSearchResItem {
    dt_big: CommodityLabelBigItem[];
    dt_mid: CommodityLabelMidItem[];
    dt_small: CommodityLabelSmallItem[];
}

/**
 * @description 零件標籤查詢
 */
export const CommodityLabelSearch = async (
    obj: CommodityLabelSearchParams
): Promise<ResultObj<CommodityLabelSearchResItem>> => {
    return await allFetch('Commodity/CommodityLabelSearch', obj);
};

/**
 * @description 零件標籤修改參數
 * @param {string} cl_id 流水號
 * @param {string} label_big 第一層流水號
 * @param {string} label_mid 第二層流水號
 * @param {string} content 標籤名稱
 * @param {string} visible 是否可見
 * @param {string} is_front 是否前台用
 * @param {string} is_nav 是否放在nav
 */
export interface CommodityLabelUpdateParams {
    cl_id: string;
    label_big?: string;
    label_mid?: string;
    content: string;
    visible: string;
    is_front: string;
    is_nav: string;
}

/**
 * @description 零件標籤修改
 */
export const CommodityLabelUpdate = async (
    obj: CommodityLabelUpdateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityLabelUpdate', obj);
};

/**
 * @description 零件標籤刪除參數
 * @param {string} cl_id 流水號
 */
export interface CommodityLabelDeleteParams {
    cl_id: string;
}

/**
 * @description 零件標籤刪除
 */
export const CommodityLabelDelete = async (
    obj: CommodityLabelDeleteParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityLabelDelete', obj);
};

/**
 * @description 零件標籤細項參數
 * @param {string} cl_id 流水號
 */
export interface CommodityLabelDetailParams {
    cl_id: string;
}

/**
 * @description 零件標籤細項
 */
export const CommodityLabelDetail = async (
    obj: CommodityLabelDetailParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityLabelDetail', obj);
};

/**
 * @description 零件新建參數
 * @param {string} commodity_code 零件編號
 * @param {string} commodity_name 零件名稱
 * @param {string} type 零件類別
 * @param {string} label_big 大標籤
 * @param {string} label_mid 中標籤
 * @param {string} label_small 小標籤
 * @param {string} unit 單位
 * @param {string} note 備註
 * @param {string} buy_retail_price 零售買入價
 * @param {string} buy_wholesale_price 批發買入價
 * @param {string} sale_price 售價
 * @param {string} suggested_price 建議售價
 * @param {string} safe_storage 安全庫存
 * @param {string} need_calc_stock 是否需要計算庫存
 * @param {string} iscombination 是否組合零件
 * @param {string} on_shelf_time 上架時間
 * @param {string} off_shelf_time 下架時間
 * @param {string} ccam_name_1 屬性主項名稱1
 * @param {string} ccad_name_1 屬性細項名稱1
 * @param {string} ccam_name_2 屬性主項名稱2
 * @param {string} ccad_name_2 屬性細項名稱2
 * @param {string} weight 重量(公斤)
 * @param {string} introduction 零件介紹
 * @param {string} seq 排序
 */
export interface CommodityCreateParams {
    commodity_code: string;
    commodity_name: string;
    type: string;
    label_big: string;
    label_mid: string;
    label_small: string;
    unit: string;
    note: string;
    buy_retail_price: string;
    buy_wholesale_price: string;
    sale_price: string;
    suggested_price: string;
    safe_storage: string;
    need_calc_stock: string;
    iscombination: string;
    warehouse_id: string;
    on_shelf_time: string;
    off_shelf_time: string;
    ccam_name_1: string;
    ccad_name_1: string;
    ccam_name_2: string;
    ccad_name_2: string;
    weight: string;
    introduction: string;
    seq: string;
    [key: string]: string;
}

/**
 * @description 零件新建
 */
export const CommodityCreate = async (
    obj: CommodityCreateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityCreate', obj);
};

/**
 * @description 零件查詢參數
 * @param {string} commodity_code 零件編號
 * @param {string} commodity_name 零件名稱
 * @param {string} type 零件類別
 * @param {string} label_big 大標籤
 * @param {string} label_mid 中標籤
 * @param {string} label_small 小標籤
 * @param {string} visible 是否可見
 * @param {string} iscombination 是否組合零件
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 * @param {string} excel 是否匯出
 */
export interface CommoditySearchParams {
    commodity_code: string;
    commodity_name: string;
    type: "配件" | "零件" | "設備";
    label_big: string;
    label_mid: string;
    label_small: string;
    visible: "Y" | "N" | "";
    iscombination: "Y" | "N" | "";
    page: number;
    num_per_page: number;
    excel: "Y" | "N";
}

/**
 * @description 零件查詢結果
 */
export interface CommoditySearchResItem {
    commodity_id: string;
    commodity_code: string;
    commodity_name: string;
    type: string;
    note: string;
    visible: string;
    iscombination: string;
    CommodityXCompany: string;
    cre_time: string;
    label_big: string;
    content_big: string;
    seq: number;
}

/**
 * @description 零件查詢
 */
export const CommoditySearch = async (
    obj: CommoditySearchParams
): Promise<ResultObj<CommoditySearchResItem[]>> => {
    return await allFetch('Commodity/CommoditySearch', obj);
};

/**
 * @description 零件修改參數
 * @param {string} commodity_id 零件主項流水號
 * @param {string} commodity_code 零件編號
 * @param {string} commodity_name 零件名稱
 * @param {string} label_big 大標籤
 * @param {string} label_mid 中標籤
 * @param {string} label_small 小標籤
 * @param {string} unit 單位
 * @param {string} note 備註
 * @param {string} visible 是否可見
 * @param {string} need_calc_stock 是否需要計算庫存
 * @param {string} iscombination 是否組合零件
 * @param {string} weight 重量(公斤)
 * @param {string} introduction 零件介紹
 * @param {string} seq 排序
 */
export interface CommodityUpdateParams {
    commodity_id: string;
    commodity_code?: string;
    commodity_name: string;
    label_big?: string;
    label_mid?: string;
    label_small?: string;
    unit: string;
    note?: string;
    visible: string;
    need_calc_stock?: string;
    iscombination: string;
    weight?: string;
    introduction?: string;
    seq?: string;
}

/**
 * @description 零件修改
 */
export const CommodityUpdate = async (
    obj: CommodityUpdateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityUpdate', obj);
};

/**
 * @description 零件刪除參數
 * @param {string} commodity_id 零件主項流水號
 */
export interface CommodityDeleteParams {
    commodity_id: string;
}

/**
 * @description 零件刪除
 */
export const CommodityDelete = async (
    obj: CommodityDeleteParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityDelete', obj);
};

/**
 * @description 零件細項參數
 * @param {string} commodity_id 零件主項流水號
 */
export interface CommodityDetailParams {
    commodity_id: string;
}
/**
 * @description 零件細項(回傳)
 */
export interface CommodityDetailResItem {
    dt: Array<CommodityDetailDtResItem>,
    dt_combination: Array<object>//暫無使用
}

export interface CommodityDetailDtResItem {
    commodity_code: string;
    commodity_id: string;
    commodity_name: string;
    commodity_type: string;
    introduction: string;
    iscombination: string;
    label_big: string;
    label_mid: string;
    label_small: string;
    need_calc_stock: string;
    note: string;
    seq: number;
    type: string;
    unit: string;
    visible: "Y" | "N";
    weight: number;
}

/**
 * @description 零件細項
 */
export const CommodityDetail = async (
    obj: CommodityDetailParams
): Promise<ResultObj<CommodityDetailResItem>> => {
    return await allFetch('Commodity/CommodityDetail', obj);
};

/**
 * @description 查詢零件上架時段參數
 * @param {string} commodity_id 零件主項流水號
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface CommodityOnShelfPeriodSearchParams {
    commodity_id: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 查詢零件上架時段
 */
export const CommodityOnShelfPeriodSearch = async (
    obj: CommodityOnShelfPeriodSearchParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityOnShelfPeriodSearch', obj);
};

/**
 * @description 零件綁車型規格查詢參數
 * @param {string} commodity_id 零件主項流水號
 * @param {string} ct_id 車型流水號
 * @param {string} ctf_id 車型規格流水號
 */
export interface CommodityXCarTypeXFormatSearchParams {
    commodity_id?: string;
    ct_id?: string;
    ctf_id?: string;
}

/**
 * @description 零件綁車型規格查詢結果
 */
export interface CommodityXCarTypeXFormatSearchResItem {
    commodity_id: string;
    ct_id: string;
    ctf_id: string;
}

/**
 * @description 零件綁車型規格查詢
 */
export const CommodityXCarTypeXFormatSearch = async (
    obj: CommodityXCarTypeXFormatSearchParams
): Promise<ResultObj<CommodityXCarTypeXFormatSearchResItem[]>> => {
    return await allFetch('Commodity/CommodityXCarTypeXFormatSearch', obj);
};

/**
 * @description 零件綁車型規格更新參數
 * @param {string} commodity_id 零件主項流水號
 * @param {Array<CarTypeXFormatParams>} cart_type_formats 車型&規格流水號列表
 */
export interface CommodityXCarTypeXFormatUpdateParams {
    commodity_id: string;
    cart_type_formats: Array<CarTypeXFormatParams>;
}

export interface CarTypeXFormatParams {
    ct_id: string;
    ctf_id?: string;
}

/**
 * @description 零件綁車型規格更新
 */
export const CommodityXCarTypeXFormatUpdate = async (
    obj: CommodityXCarTypeXFormatUpdateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityXCarTypeXFormatUpdate', obj);
};

/**
 * @description 零件綁廠商查詢參數
 * @param {string} commodity_id 零件主項流水號
 * @param {string} company_id 廠商流水號列表
 */
export interface CommodityXCompanySearchParams {
    commodity_id?: string;
    company_id?: string;
}

/**
 * @description 零件綁廠商查詢結果
 */
export interface CommodityXCompanySearchResItem {
    commodity_id: string;
    company_id: string;
}

/**
 * @description 零件綁廠商查詢
 */
export const CommodityXCompanySearch = async (
    obj: CommodityXCompanySearchParams
): Promise<ResultObj<CommodityXCompanySearchResItem[]>> => {
    return await allFetch('Commodity/CommodityXCompanySearch', obj);
};

/**
 * @description 零件綁廠商更新參數
 * @param {string} commodity_id 零件主項流水號
 * @param {Array<string>} company_id 廠商流水號列表
 */
export interface CommodityXCompanyUpdateParams {
    commodity_id: string;
    company_id: Array<string>;
}

/**
 * @description 零件綁廠商更新
 */
export const CommodityXCompanyUpdate = async (
    obj: CommodityXCompanyUpdateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityXCompanyUpdate', obj);
};

/**
 * @description 查詢零件自訂屬性主項參數
 * @param {string} commodity_id 零件流水號
 */
export interface CommodityCustomAttriMasterSearchParams {
    commodity_id: string;
}
export interface CommodityCustomAttriMasterSearchResItem {
    ccam_id: string;
    ccam_name: string;
    commodity_id: string;
}

/**
 * @description 查詢零件自訂屬性主項
 */
export const CommodityCustomAttriMasterSearch = async (
    obj: CommodityCustomAttriMasterSearchParams
): Promise<ResultObj<CommodityCustomAttriMasterSearchResItem[]>> => {
    return await allFetch('Commodity/CommodityCustomAttriMasterSearch', obj);
};

/**
 * @description 修改零件自訂屬性主項參數
 * @param {string} ccam_id 主項流水號
 * @param {string} ccam_name 屬性主項名稱
 */
export interface CommodityCustomAttriMasterUpdateParams {
    ccam_id: string;
    ccam_name: string;
}

/**
 * @description 修改零件自訂屬性主項
 */
export const CommodityCustomAttriMasterUpdate = async (
    obj: CommodityCustomAttriMasterUpdateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityCustomAttriMasterUpdate', obj);
};

/**
 * @description 零件自訂屬性主項細項參數
 * @param {string} ccam_id 主項流水號
 */
export interface CommodityCustomAttriMasterDetailParams {
    ccam_id: string;
}

/**
 * @description 零件自訂屬性主項細項
 */
export const CommodityCustomAttriMasterDetail = async (
    obj: CommodityCustomAttriMasterDetailParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityCustomAttriMasterDetail', obj);
};

/**
 * @description 新增零件自訂屬性細項參數
 * @param {string} ccam_id 主項流水號
 * @param {string} ccad_name 屬性細項名稱
 */
export interface CommodityCustomAttriDetailCreateParams {
    ccam_id: string;
    ccad_name: string;
}

/**
 * @description 新增零件自訂屬性細項
 */
export const CommodityCustomAttriDetailCreate = async (
    obj: CommodityCustomAttriDetailCreateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityCustomAttriDetailCreate', obj);
};

/**
 * @description 查詢零件自訂屬性細項參數
 * @param {string} ccam_id 主項流水號
 */
export interface CommodityCustomAttriDetailSearchParams {
    ccam_id: string;
}

export interface CommodityCustomAttriDetailSearchResItem {
    ccad_id: string;
    ccad_name: string;
    ccam_id: string;
}

/**
 * @description 查詢零件自訂屬性細項
 */
export const CommodityCustomAttriDetailSearch = async (
    obj: CommodityCustomAttriDetailSearchParams
): Promise<ResultObj<CommodityCustomAttriDetailSearchResItem[]>> => {
    return await allFetch('Commodity/CommodityCustomAttriDetailSearch', obj);
};

/**
 * @description 修改零件自訂屬性細項參數
 * @param {string} ccad_id 細項流水號
 * @param {string} ccad_name 屬性細項名稱
 */
export interface CommodityCustomAttriDetailUpdateParams {
    ccad_id: string;
    ccad_name: string;
}

/**
 * @description 修改零件自訂屬性細項
 */
export const CommodityCustomAttriDetailUpdate = async (
    obj: CommodityCustomAttriDetailUpdateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityCustomAttriDetailUpdate', obj);
};

/**
 * @description 刪除零件自訂屬性細項參數
 * @param {string} ccad_id 細項流水號
 */
export interface CommodityCustomAttriDetailDeleteParams {
    ccad_id: string;
}

/**
 * @description 刪除零件自訂屬性細項
 */
export const CommodityCustomAttriDetailDelete = async (
    obj: CommodityCustomAttriDetailDeleteParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityCustomAttriDetailDelete', obj);
};

/**
 * @description 查詢零件自訂屬性細項參數
 * @param {string} ccad_id 細項流水號
 */
export interface CommodityCustomAttriDetailDetailParams {
    ccad_id: string;
}

/**
 * @description 查詢零件自訂屬性細項
 */
export const CommodityCustomAttriDetailDetail = async (
    obj: CommodityCustomAttriDetailDetailParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityCustomAttriDetailDetail', obj);
};

/**
 * @description 自訂屬性查詢(可用列表)參數
 * @param {string} commodity_id 細項零件流水號流水號
 * @param {string} commodity_code 零件編號
 * @param {string} commodity_name 零件名稱
 * @param {string} type 零件類別
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface CommodityCustomAttriListParams {
    commodity_id?: string;
    commodity_code?: string;
    commodity_name?: string;
    type?: string;
    page?: number;
    num_per_page?: number;
};

/**
 * @description 自訂屬性查詢(可用列表)結果
 */
export interface CommodityCustomAttriListResItem {
    ccad_id: string;
    ccad_name: string;
    ccam_id: string;
    ccam_name: string;
    commodity_id: string;
};

/**
 * @description 自訂屬性查詢(可用列表)
 */
export const CommodityCustomAttriList = async (
    obj: CommodityCustomAttriListParams = {}
): Promise<ResultObj<CommodityCustomAttriListResItem[]>> => {
    return await allFetch('Commodity/CommodityCustomAttriList', obj);
};

/**
 * @description 新增零件組合內容參數
 * @param {string} commodity_id 零件主項流水號
 * @param {string} ccad_id_1 自定義屬性細項流水號1
 * @param {string} ccad_id_2 自定義屬性細項流水號2
 * @param {Array<CommodityCombinationDetail_objParams>} contents 內容零件們
 */
export interface CommodityCombinationCreateParams {
    commodity_id: string;
    ccad_id_1: string;
    ccad_id_2: string;
    contents: Array<CommodityCombinationDetail_objParams>;
};

export interface CommodityCombinationDetail_objParams {
    commodity_id: string;
    uc_id: string;
    ccad_id_1: string;
    ccad_id_2: string;
    count: string;
}

/**
 * @description 新增零件組合內容
 */
export const CommodityCombinationCreate = async (
    obj: CommodityCombinationCreateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityCombinationCreate', obj);
};

/**
 * @description 修改零件組合內容(數量)參數
 * @param {string} ccd_id 流水號
 * @param {string} count 數量
 */
export interface CommodityCombinationUpdateParams {
    ccd_id: string;
    count: string;
}

/**
 * @description 修改零件組合內容
 */
export const CommodityCombinationUpdate = async (
    obj: CommodityCombinationUpdateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityCombinationUpdate', obj);
};

/**
 * @description 刪除零件組合內容參數
 * @param {string} ccd_id 流水號
 */
export interface CommodityCombinationDeleteParams {
    ccd_id: string;
}

/**
 * @description 刪除零件組合內容
 */
export const CommodityCombinationDelete = async (
    obj: CommodityCombinationDeleteParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityCombinationDelete', obj);
};

/**
 * @description 組合零件參數
 * @param {string} commodity_id 零件主項流水號
 * @param {string} uc_id 單位轉換流水號
 * @param {string} ccad_id_1 自定義屬性細項流水號1
 * @param {string} ccad_id_2 自定義屬性細項流水號2
 * @param {string} count 組合數量
 * @param {string} warehouse_id 倉庫流水號
 */
export interface CommodityAssembleParams {
    commodity_id: string;
    uc_id: string;
    ccad_id_1: string;
    ccad_id_2: string;
    count: string;
    warehouse_id: string;
}

/**
 * @description 組合零件
 */
export const CommodityAssemble = async (
    obj: CommodityAssembleParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityAssemble', obj);
};

/**
 * @description 拆解零件參數
 * @param {string} commodity_id 零件主項流水號
 * @param {string} uc_id 單位轉換流水號
 * @param {string} ccad_id_1 自定義屬性細項流水號1
 * @param {string} ccad_id_2 自定義屬性細項流水號2
 * @param {string} count 組合數量
 * @param {string} warehouse_id 倉庫流水號
 */
export interface CommodityDisassembleParams {
    commodity_id: string;
    uc_id: string;
    ccad_id_1: string;
    ccad_id_2: string;
    count: string;
    warehouse_id: string;
}

/**
 * @description 拆解零件
 */
export const CommodityDisassemble = async (
    obj: CommodityDisassembleParams
): Promise<ResultObj> => {
    return await allFetch("Commodity/CommodityDisassemble", obj);
};

/**
 * @description 零件匯入參數
 * @param {string} commodity_code 零件編號
 * @param {string} commodity_name 零件名稱
 * @param {string} type 零件類別
 * @param {string} label_big 大標籤名稱
 * @param {string} label_mid 中標籤名稱
 * @param {string} label_small 小標籤名稱
 * @param {string} on_shelf_time 上架時間
 * @param {string} off_shelf_time 下架時間
 * @param {string} unit 單位名稱
 * @param {string} note 備註
 * @param {string} buy_retail_price 零售買入價
 * @param {string} buy_wholesale_price 批發買入價
 * @param {string} sale_price 售價
 * @param {string} suggested_price 建議售價
 * @param {string} safe_storage 安全庫存
 * @param {string} need_calc_stock 是否需要計算庫存
 * @param {string} iscombination 是否組合零件
 * @param {string} ccam_name_1 屬性主項名稱1
 * @param {string} ccad_name_1 屬性細項名稱1
 * @param {string} ccam_name_2 屬性主項名稱2
 * @param {string} ccad_name_2 屬性細項名稱2
 * @param {string} company 廠商名稱
 */
export interface CommodityImportParams {
    commodity_code: string;
    commodity_name: string;
    type: string;
    label_big: string;
    label_mid: string;
    label_small: string;
    on_shelf_time: string;
    off_shelf_time: string;
    unit: string;
    note: string;
    buy_retail_price: string;
    buy_wholesale_price: string;
    sale_price: string;
    suggested_price: string;
    safe_storage: string;
    need_calc_stock: string;
    iscombination: string;
    ccam_name_1: string;
    ccad_name_1: string;
    ccam_name_2: string;
    ccad_name_2: string;
    company: string;
}

/**
 * @description 零件匯入
 */
export const CommodityImport = async (
    obj: CommodityImportParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/CommodityImport', obj);
};

/**
 * @description  零件列表參數
 * @param {string} commodity_code 零件編號
 * @param {string} commodity_name 零件名稱
 * @param {string} type 零件類別
 * @param {string} company_id 廠商流水號
 * @param {string} label_big 大標籤
 * @param {string} label_mid 中標籤
 * @param {string} label_small 小標籤
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 * @param {string} include_avg_month 是否查詢平均採購數量
 * @param {string} include_purchased_day 是否查詢最近一次進貨日期
 */
export interface CommodityListParams {
    commodity_code: string;
    commodity_name: string;
    type: string;
    company_id: string;
    label_big: string;
    label_mid: string;
    label_small: string;
    page: number;
    num_per_page: number;
    include_avg_month?: string;
    include_purchased_day?: string;
    [key: string]: string | number | undefined;
};

/**
 * @description 零件列表結果
 */
export interface CommodityListResItem {
    ccam_id_1: string;
    ccam_id_2: string;
    ccam_name_1: string;
    ccam_name_2: string;
    commodity_code: string;
    commodity_id: string;
    commodity_name: string;
    seq: number;
    type: string;
    unit: string;
    stock_count: number;
    purchased_in_days?: string;
    avg_purchase_qty_month?: string;
    [key: string]: string | number | undefined;
};

/**
 * @description  零件列表
 */
export const CommodityList = async (
    obj: CommodityListParams
): Promise<ResultObj<CommodityListResItem[]>> => {
    return await allFetch('Commodity/CommodityList', obj);
};

/**
 * @description 零件庫存查詢參數
 * @param {string} commodity_id 零件主項流水號
 * @param {string} uc_id 單位換算流水號
 * @param {string} ccad_id_1 自定義屬性細項流水號1
 * @param {string} ccad_id_2 自定義屬性細項流水號2
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface CommodityStorageParams {
    commodity_id?: string;
    uc_id?: string;
    ccad_id_1?: string;
    ccad_id_2?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 零件庫存查詢結果
 */
export interface CommodityStorageResItem {
    commodity_id: string;
    commodity_code: string;
    commodity_name: string;
    convert_unit_name: string;
    convert_count: string;
    basic_unit_name: string;
    basic_count: string;
    uc_id: string;
    ccad_name_1: string;
    ccad_name_2: string;
    count: number;
    safe_storage: string;
    need_calc_stock: string;
    cost: number;
    avg_sale_price: number;
    label_big_name: string;
    label_mid_name: string;
    label_small_name: string;
    stock_count: number;
}

/**
 * @description 零件庫存查詢
 */
export const CommodityStorage = async (
    obj: CommodityStorageParams
): Promise<ResultObj<CommodityStorageResItem[]>> => {
    return await allFetch('Commodity/CommodityStorage', obj);
};

/**
 * @description 單位轉換查詢參數
 * @param {string} commodity_id 零件主項流水號
 * @param {string} visible 是否可見
 */
export interface UnitConversionSearchParams {
    commodity_id: string;
    visible?: string;
}

/**
 * @description 單位轉換查詢結果
 */
export type UnitConversionSearchResItem = {
    basic_count: number;
    basic_unit_id: string;
    basic_unit_name: string;
    buy_retail_price: number;
    buy_wholesale_price: number;
    commodity_id: string;
    convert_count: number;
    convert_unit_id: string;
    convert_unit_name: string;
    safe_storage: number;
    sale_price: number;
    uc_id: string;
    warehouse_id: string;
    warehouse_name: string;
    warehouse_storage_space: string;
    visible: "Y" | "N";
}



/**
 * @description 單位轉換查詢
 */
export const UnitConversionSearch = async (
    obj: UnitConversionSearchParams
): Promise<ResultObj<UnitConversionSearchResItem[]>> => {
    return await allFetch('Commodity/UnitConversionSearch', obj);
};

/**
 * @description 單位轉換修改參數
 * @param {string} uc_id 單位換算流水號
 * @param {string} commodity_id 零件主項流水號
 * @param {string} convert_unit_id 轉換單位流水號
 * @param {string} convert_count 轉換單位數量
 * @param {string} basic_unit_id 基本單位流水號
 * @param {string} basic_count 基本單位數量
 * @param {string} safe_storage 安全庫存
 * @param {string} visible 是否可見
 * @param {string} buy_retail_price 零售買入價
 * @param {string} buy_wholesale_price 批發買入價
 * @param {string} sale_price 售價
 */
export interface UnitConversionUpdateParams {
    uc_id: string;
    commodity_id: string;
    convert_unit_id: string;
    convert_count: number;
    basic_unit_id: string;
    basic_count: number;
    safe_storage?: number;
    visible: string;
    buy_retail_price?: number;
    buy_wholesale_price?: number;
    sale_price?: number;
    warehouse_id?: string;
}

/**
 * @description 單位轉換修改
 */
export const UnitConversionUpdate = async (
    obj: UnitConversionUpdateParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/UnitConversionUpdate', obj);
};

/**
 * @description 單位轉換細項參數
 * @param {string} uc_id 單位換算流水號
 * @returns 
 */
export interface UnitConversionDetailParams {
    uc_id: string;
}

/**
 * @description 單位轉換細項結果
 */
export interface UnitConversionDetailResItem {
    uc_id: string;
    commodity_id: string;
    convert_unit_id: string;
    convert_count: number;
    basic_unit_id: string;
    basic_count: number;
    safe_storage: number;
    buy_retail_price: number;
    buy_wholesale_price: number;
    sale_price: number;
    warehouse_id: string;
    warehouse_name: string;
    warehouse_storage_space: string;
    visible: string;
    [key: string]: string | number;
}

/**
 * @description 單位轉換細項
 */
export const UnitConversionDetail = async (
    obj: UnitConversionDetailParams
): Promise<ResultObj<UnitConversionDetailResItem[]>> => {
    return await allFetch('Commodity/UnitConversionDetail', obj);
};

/**
 * @description 單位轉換列表參數
 * @param {string} commodity_id 零件主項流水號
 * @param {string} commodity_code 零件編號
 * @param {string} commodity_name 零件名稱
 * @param {string} type 零件類別
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface UnitConversionListParams {
    commodity_id?: string;
    commodity_code?: string;
    commodity_name?: string;
    type?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 單位轉換列表結果
 */
export interface UnitConversionListResItem {
    basic_count: string,
    basic_unit_id: string,
    basic_unit_name: string,
    buy_retail_price: string,
    buy_wholesale_price: string,
    commodity_code: string,
    commodity_id: string,
    commodity_name: string,
    convert_count: string,
    convert_unit_id: string,
    convert_unit_name: string,
    safe_storage: string,
    sale_price: string,
    uc_id: string,
    visible: string,
    warehouse_id: string,
    warehouse_name: string,
    warehouse_storage_space: string,
}

/**
 * @description 單位轉換列表
 */
export const UnitConversionList = async (
    obj: UnitConversionListParams
): Promise<ResultObj<UnitConversionListResItem[]>> => {
    return await allFetch("Commodity/UnitConversionList", obj);
};

/**
 * @description 單位查詢參數
 * @param {string} unit_name 單位名稱
 * @param {string} visible 是否可見
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface UnitSearchParams {
    unit_name?: string;
    visible?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 單位查詢
 */
export const UnitSearch = async (
    obj: UnitSearchParams
): Promise<ResultObj> => {
    return await allFetch('Commodity/UnitSearch', obj);
};